import React, { useState } from 'react';
import { Linking, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import source from '../../CHANGELOG.md';
import { useLanguage } from '../../contexts/LanguageContext';
import { parseChangelog } from '../../utils/changelog';

const history = parseChangelog(source);
const currentVersion = Constants.expoConfig?.version ?? '';

function MarkdownText({ value }: { value: string }) {
  // Render the inline formatting used by CHANGELOG.md without HTML or remote content.
  const parts = value.split(/(\[[^\]]+\]\(https?:\/\/[^\s)]+\)|`[^`]+`|\*\*[^*]+\*\*)/g);
  return <Text style={styles.entryText} selectable>{parts.map((part, index) => {
    const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
    if (link) return <Text key={index} accessibilityRole="link" style={styles.link}
      onPress={() => { void Linking.openURL(link[2]); }}>{link[1]}</Text>;
    if (part.startsWith('`')) return <Text key={index} style={styles.code}>{part.slice(1, -1)}</Text>;
    if (part.startsWith('**')) return <Text key={index} style={styles.bold}>{part.slice(2, -2)}</Text>;
    return part;
  })}</Text>;
}

function Entries({ entries }: { entries: string[] }) {
  return <View style={styles.entries}>{entries.map((entry, index) => <View key={index} style={styles.entry}>
    <Text style={styles.bullet}>•</Text><MarkdownText value={entry} />
  </View>)}</View>;
}

function Accordion({ label, detail, open, onPress }: {
  label: string; detail?: string; open: boolean; onPress: () => void;
}) {
  return <TouchableOpacity accessibilityRole="button" accessibilityLabel={detail ? `${label}, ${detail}` : label} accessibilityState={{ expanded: open }}
    aria-expanded={open} onPress={onPress} style={styles.accordion}>
    <View style={styles.accordionText}>
      <Text style={styles.groupTitle}>{label}</Text>
      {detail ? <Text style={styles.muted}>{detail}</Text> : null}
    </View>
    <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={20} color="#526579" />
  </TouchableOpacity>;
}

export default function ChangelogScreen() {
  const { t } = useLanguage();
  const latest = history.majors[0];
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    [`major-${latest?.version}`]: true,
    [`minor-${latest?.minors[0]?.version}`]: true,
  });
  const toggle = (key: string) => setExpanded(previous => ({ ...previous, [key]: !previous[key] }));
  return <ScrollView style={styles.container} contentContainerStyle={styles.content}>
    <Text accessibilityRole="header" style={styles.title}>{t('changelog.title')}</Text>
    <Text style={styles.intro}>{t('changelog.intro')}</Text>
    <View style={styles.version}><Ionicons name="information-circle-outline" size={18} color="#0066CC" />
      <Text style={styles.versionText}>{t('contact.versionInfo', currentVersion)}</Text></View>
    {history.development.length > 0 ? <View style={styles.card}>
      <Accordion label={t('changelog.development')} open={!!expanded.development}
        onPress={() => toggle('development')} />
      {expanded.development ? <Entries entries={history.development} /> : null}
    </View> : null}
    {history.majors.map(major => {
      const majorKey = `major-${major.version}`;
      return <View key={majorKey} style={styles.card}>
        <Accordion label={t('changelog.major', String(major.version))}
          detail={t('changelog.minorCount', String(major.minors.length))}
          open={!!expanded[majorKey]} onPress={() => toggle(majorKey)} />
        {expanded[majorKey] ? <View style={styles.minors}>{major.minors.map(minor => {
          const minorKey = `minor-${minor.version}`;
          return <View key={minorKey} style={styles.minor}>
            <Accordion label={`${minor.version}.x`} detail={t('changelog.releaseCount', String(minor.releases.length))}
              open={!!expanded[minorKey]} onPress={() => toggle(minorKey)} />
            {expanded[minorKey] ? minor.releases.map(release => <View key={release.version} style={styles.release}>
              <View style={styles.releaseHeader}>
                <Text accessibilityRole="header" style={styles.releaseTitle}>{release.version}</Text>
                {release.version === currentVersion ? <Text style={styles.badge}>{t('changelog.current')}</Text> : null}
                {release.date ? <Text style={styles.date}>{release.date}</Text> : null}
              </View>
              <Entries entries={release.entries} />
            </View>) : null}
          </View>;
        })}</View> : null}
      </View>;
    })}
    {!history.majors.length && !history.development.length ? <Text style={styles.intro}>{t('changelog.empty')}</Text> : null}
  </ScrollView>;
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  content: { width: '100%', maxWidth: 860, alignSelf: 'center', padding: 16, paddingBottom: 40, gap: 12 },
  title: { fontSize: 26, fontWeight: '700', color: '#25364a', marginTop: 8 },
  intro: { fontSize: 14, lineHeight: 22, color: '#526579' },
  version: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 },
  versionText: { color: '#0066CC', fontSize: 13, fontWeight: '600' },
  card: { backgroundColor: '#fff', borderRadius: 12, borderWidth: 1, borderColor: '#dfe5ed', overflow: 'hidden' },
  accordion: { minHeight: 52, flexDirection: 'row', alignItems: 'center', padding: 14, gap: 12 },
  accordionText: { flex: 1, gap: 4 },
  groupTitle: { fontSize: 16, fontWeight: '600', color: '#25364a' },
  muted: { fontSize: 12, color: '#526579' },
  minors: { paddingLeft: 12, paddingRight: 12, paddingBottom: 12 },
  minor: { borderTopWidth: 1, borderColor: '#e7ebf1' },
  release: { borderLeftWidth: 2, borderColor: '#c5d7eb', marginLeft: 12, marginBottom: 14, paddingLeft: 12 },
  releaseHeader: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, paddingTop: 6, paddingRight: 12 },
  releaseTitle: { fontSize: 15, fontWeight: '700', color: '#25364a' },
  badge: { color: '#13613b', backgroundColor: '#e8f5ed', fontSize: 11, paddingHorizontal: 7, paddingVertical: 3, borderRadius: 6 },
  date: { fontSize: 12, color: '#526579' },
  entries: { padding: 12, gap: 8 },
  entry: { flexDirection: 'row', gap: 8 },
  bullet: { color: '#66788b', lineHeight: 22 },
  entryText: { flex: 1, fontSize: 14, lineHeight: 22, color: '#36495d' },
  code: { backgroundColor: '#edf2f7', fontFamily: 'monospace', fontSize: 12 },
  bold: { fontWeight: '700' },
  link: { color: '#0066CC', textDecorationLine: 'underline' },
});
